import {
  Delete as DeleteIcon,
  MoreVert,
  Sort,
  SortByAlpha,
} from "@mui/icons-material";
import {
  Box,
  Checkbox,
  CircularProgress,
  Container,
  IconButton,
  ListItemIcon,
  Menu,
  MenuItem,
  SvgIconTypeMap,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
} from "@mui/material";
import { OverridableComponent } from "@mui/material/OverridableComponent";
import React, { useEffect, useState } from "react";
import SelectToolbar from "./SelectToolbarV2";
import {
  filterData,
  getDataField,
  sortData,
  type DataField,
  type SortOptions,
} from "../../utils/tableUtils";

// Re-exported: DataField was declared here before the search/sort logic moved
// to core/utils/tableUtils, and ~20 page components import it from this path.
export type { DataField } from "../../utils/tableUtils";

export interface TableTemplateProps<T> {
  tableKeyPrefix: string;
  showSettingsHeader?: boolean;
  useHeader?: boolean;
  usePagination?: boolean;
  useSort?: boolean;
  useDefaultActions?: boolean;
  setUsePagination?: React.Dispatch<React.SetStateAction<boolean>>;
  headerFields?: React.ReactNode[];
  data?: T[];
  loading?: boolean;
  setData: React.Dispatch<React.SetStateAction<T[]>>;
  searchOptions?: {
    searchValue?: string;
    conditions?: ((row: T, searchValue: string) => boolean)[];
  };
  dataFields?: DataField<T>[];
  /** single row custom actions (Delete action is added automatically if onDelete is defined) */
  actions?: ActionMenuProps<T>["actions"];
  /** label for actions cell */
  actionsLabel?: string;
  selected?: T[];
  /** define which field of T will act as key (used for comparison) */
  keyField?: keyof T;
  /** if data is not flat and has nested objects, this function will be used to extract the key field value
   * use this instead of keyField
   */
  keyFieldValueExtractor?: (row: T) => string | number;
  /** react setState function for setting selected items */
  setSelected?: React.Dispatch<React.SetStateAction<T[]>>;
  paginationOptions?: {
    rowsPerPageOptions?: number[];
  };
  /** calls onDelete and passes an update function that updates the data table accordingly, must be called on a successful delete operation */
  onDelete?: (
    rows: T[],
    updateTable?: (except?: T[]) => Promise<void>
  ) => Promise<void>;
  onError(e: Error): void;
  /** Custom component to be shown as collapsible row when adding new data */
  FormComponent?: React.ReactNode;
  extraToolbar?: React.ReactNode;
  customActions?: ActionProps<any>[];
  /** Indicates if there is more data to load */
  hasMoreData?: boolean;
  /** Callback to load more data */
  onLoadMore?: () => void;
}

const keyFieldorKeyFieldValueExtractorMissingError = new Error(
  "keyField OR keyFieldValueExtractor is required as it indicates the unique key for comparison"
);

const keyFieldAndKeyFieldValueExtractorBothDefinedError = new Error(
  "keyField and keyFieldValueExtractor cannot be defined at the same time. Use one or the other"
);

export const TableTemplate = <T,>({
  tableKeyPrefix,
  showSettingsHeader = true,
  useHeader = true,
  usePagination = true,
  useSort = true,
  useDefaultActions = true,
  setUsePagination,
  headerFields,
  data,
  setData,
  dataFields,
  actions,
  actionsLabel,
  selected = [],
  paginationOptions,
  keyField,
  keyFieldValueExtractor,
  setSelected,
  onDelete,
  loading,
  searchOptions,
  onError,
  FormComponent,
  extraToolbar,
  customActions,
  hasMoreData,
  onLoadMore,
}: TableTemplateProps<T>) => {
  if (!keyField && !keyFieldValueExtractor) {
    throw keyFieldorKeyFieldValueExtractorMissingError;
  } else if (keyField && keyFieldValueExtractor) {
    throw keyFieldAndKeyFieldValueExtractorBothDefinedError;
  }
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(
    paginationOptions?.rowsPerPageOptions?.[0] || 25
  );

  const [filteredData, setFilteredData] = useState<T[]>([]);

  const [sort, setSort] = useState<SortOptions<T> | null>(null);

  // Auto-load more data when approaching the last page
  useEffect(() => {
    if (!hasMoreData || !onLoadMore || loading) return;

    const currentData = getFilteredData();
    const totalPages = Math.ceil(currentData.length / rowsPerPage);
    const isOnLastPage = page >= totalPages - 1;

    // Load more if on the last page or within 2 pages of the end
    if (isOnLastPage || page >= totalPages - 2) {
      onLoadMore();
    }
  // Deps intentionally omitted: getFilteredData, onLoadMore are redefined on every render, so listing them would re-run this effect continuously.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, rowsPerPage, data?.length, hasMoreData, loading]);

  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  // Search and sort live in core/utils/tableUtils so they can be unit-tested
  // without MUI or a renderer. See tableUtils.test.ts.
  const getFilteredData = () => filterData(data, searchOptions);
  const getSortedData = (unsortedData: T[]) =>
    sortData(unsortedData, sort, dataFields);

  const keyFieldValueIsMatch = (row: T, comparableRow: T) => {
    if (keyField) {
      return row[keyField] === comparableRow[keyField];
    }
    if (keyFieldValueExtractor) {
      return (
        keyFieldValueExtractor(row) === keyFieldValueExtractor(comparableRow)
      );
    }
    throw keyFieldorKeyFieldValueExtractorMissingError;
  };

  const isPageEmpty = () => {
    // check if the current page is empty (for example after deleting the last item on the page)
    return (
      filteredData && filteredData.length <= page * rowsPerPage && page > 0
    );
  };

  // TODO: Make this work
  // const ifDataGoesBeyondPage = () => {
  //   // check if the data goes beyond the current page (for example after adding a new item)
  //   return (
  //     filteredData &&
  //     filteredData.length > (page + 1) * rowsPerPage &&
  //     page < filteredData.length / rowsPerPage
  //   );
  // };

  useEffect(() => {
    if (!data) {
      return;
    }
    try {
      setFilteredData(getSortedData(getFilteredData()));
    } catch (e) {
      setSort(null);
      onError(e as Error);
    }
  // Deps intentionally omitted: getFilteredData, getSortedData, onError are redefined on every render, so listing them would re-run this effect continuously.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchOptions, data, sort]);

  useEffect(() => {
    if (!filteredData) {
      return;
    }
    if (isPageEmpty()) {
      setPage(Math.max(0, Math.ceil(filteredData.length / rowsPerPage) - 1));
    }
  // Deps intentionally omitted: isPageEmpty is redefined on every render, so listing it would re-run this effect continuously.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filteredData]);

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleDelete = async (row: T) => {
    if (onDelete) {
      await onDelete([row], async () => {
        setData((prev) => prev.filter((r) => !keyFieldValueIsMatch(r, row)));
        if (setSelected) {
          setSelected((prev) =>
            prev.filter((s) => !keyFieldValueIsMatch(s, row))
          );
        }
      });
    }
  };

  const handleDeleteMultiple = async () => {
    if (onDelete) {
      await onDelete(selected, async (except?: T[]) => {
        setData((prev) =>
          prev.filter((r) => {
            if (selected.find((s) => keyFieldValueIsMatch(s, r))) {
              if (!except) {
                return false;
              }
              if (except.find((e) => keyFieldValueIsMatch(e, r))) {
                return true;
              }
              return false;
            }
            return true;
          })
        );
        if (setSelected) {
          setSelected([]);
        }
      });
    }
  };

  const handleSelected = async (row: T) => {
    if (!setSelected) {
      return;
    }
    setSelected((prev) => {
      const index = prev.findIndex((s) => keyFieldValueIsMatch(s, row));
      if (index === -1) {
        return [...prev, row];
      }
      return [...prev.slice(0, index), ...prev.slice(index + 1)];
    });
  };

  const isSelected = (row: T) => {
    return selected.find((s) => keyFieldValueIsMatch(s, row)) !== undefined;
  };

  return (
    <React.Fragment>
      <TableContainer>
        <Table
          aria-labelledby={`${tableKeyPrefix}-table`}
          sx={{
            minWidth: 600,
            borderCollapse: "separate",
            borderSpacing: "0 1rem",
          }}
        >
          {showSettingsHeader && (
            <TableHead sx={{ bgcolor: "background.paper" }}>
              <TableRow>
                <TableCell
                  colSpan={(dataFields && dataFields.length + 2) || 1}
                  // align="right"
                >
                  <Container
                    sx={{
                      flexDirection: "row",
                      display: "flex",
                      justifyContent: "flex-end",
                      alignItems: "center",
                      columnGap: "1rem",
                    }}
                  >
                    {extraToolbar && <Box>{extraToolbar}</Box>}
                    {selected.length > 0 && (
                      <SelectToolbar
                        processing={false}
                        onCancel={() => setSelected && setSelected([])}
                        onDelete={handleDeleteMultiple}
                        selected={selected}
                        actions={customActions}
                      />
                    )}
                    {useSort && (
                      <SortMenu
                        setSort={setSort}
                        dataFields={dataFields}
                        sort={sort}
                      />
                    )}
                    {setUsePagination && (
                      <Box>
                        <Switch
                          checked={usePagination}
                          onChange={(e, checked) => {
                            if (setUsePagination) {
                              setUsePagination(checked);
                            }
                          }}
                        />
                        Pagination: {usePagination ? "on" : "off"}
                      </Box>
                    )}
                  </Container>
                </TableCell>
              </TableRow>
            </TableHead>
          )}
          <TableHead>
            {FormComponent && (
              <TableRow>
                <TableCell
                  colSpan={(dataFields && dataFields.length + 2) || 1}
                  style={{ padding: 0 }}
                  align="center"
                >
                  {FormComponent}
                </TableCell>
              </TableRow>
            )}
          </TableHead>
          {useHeader && headerFields && headerFields.length > 0 && (
            <TableHead sx={{ bgcolor: "background.paper" }}>
              <TableRow>
                {setSelected && (
                  <TableCell
                    padding="checkbox"
                    sx={{
                      borderTopLeftRadius: "1rem",
                      borderBottomLeftRadius: "1rem",
                    }}
                  >
                    <Checkbox
                      color="primary"
                      checked={selected.length > 0}
                      onClick={() => {
                        if (selected.length === filteredData.length) {
                          setSelected([]);
                        } else {
                          setSelected(filteredData);
                        }
                      }}
                    />
                  </TableCell>
                )}
                {headerFields.map((header, index) => (
                  <TableCell key={`${tableKeyPrefix}-header-${index}`}>
                    {header}
                  </TableCell>
                ))}
                {useDefaultActions && (
                  <TableCell key={`${tableKeyPrefix}-header-actions`}>
                    {actionsLabel || "Actions"}
                  </TableCell>
                )}
              </TableRow>
            </TableHead>
          )}
          {useHeader &&
            !headerFields &&
            dataFields &&
            dataFields.length > 0 && (
              <TableHead sx={{ bgcolor: "background.paper" }}>
                <TableRow>
                  {setSelected && (
                    <TableCell
                      key={`${tableKeyPrefix}-header-selection`}
                    ></TableCell>
                  )}
                  {dataFields.map((header, index) => (
                    <TableCell key={`${tableKeyPrefix}-header-${index}`}>
                      {getDataField(header) as string}
                    </TableCell>
                  ))}
                  {useDefaultActions && (
                    <TableCell key={`${tableKeyPrefix}-header-actions`}>
                      {actionsLabel || "Actions"}
                    </TableCell>
                  )}
                </TableRow>
              </TableHead>
            )}

          <TableBody>
            <>
              {loading && (!data || data.length === 0) ? (
                <TableRow>
                  <TableCell
                    colSpan={(dataFields && dataFields.length + 2) || 1}
                    align="center"
                  >
                    Loading... <CircularProgress size={14} />
                  </TableCell>
                </TableRow>
              ) : (
                <>
                  {usePagination
                    ? data &&
                      dataFields &&
                      filteredData
                        .slice(
                          page * rowsPerPage,
                          page * rowsPerPage + rowsPerPage
                        )
                        .map((dataRow, index) => (
                          <TableRowTemplate
                            useDefaultActions={useDefaultActions}
                            onDelete={onDelete ? handleDelete : undefined}
                            onSelected={
                              setSelected ? handleSelected : undefined
                            }
                            selected={isSelected(dataRow)}
                            key={`${tableKeyPrefix}-row-${index}`}
                            dataRow={dataRow}
                            dataFields={dataFields}
                            actions={actions}
                          />
                        ))
                    : data &&
                      dataFields &&
                      filteredData.map((dataRow, index) => (
                        <TableRowTemplate
                          useDefaultActions={useDefaultActions}
                          onDelete={onDelete ? handleDelete : undefined}
                          onSelected={setSelected ? handleSelected : undefined}
                          selected={isSelected(dataRow)}
                          key={`${tableKeyPrefix}-row-${index}`}
                          dataRow={dataRow}
                          dataFields={dataFields}
                          actions={actions}
                        />
                      ))}
                </>
              )}
              {loading && data && data.length > 0 && (
                <TableRow>
                  <TableCell
                    colSpan={(dataFields && dataFields.length + 2) || 1}
                    align="center"
                    sx={{ py: 2 }}
                  >
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        gap: 1,
                      }}
                    >
                      Loading... <CircularProgress size={14} />
                    </Box>
                  </TableCell>
                </TableRow>
              )}
            </>
          </TableBody>
        </Table>
      </TableContainer>
      {usePagination && (
        <TablePagination
          rowsPerPageOptions={
            paginationOptions?.rowsPerPageOptions || [5, 10, 25, 50]
          }
          component="div"
          count={filteredData?.length || 0}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
        />
      )}
    </React.Fragment>
  );
};

interface TableRowTemplateProps<T> {
  dataRow: T;
  dataFields: DataField<T>[];
  actions: ActionMenuProps<T>["actions"];
  selected?: boolean;
  onSelected?: (row: T) => void;
  onDelete?: (row: T) => Promise<void>;
  useDefaultActions?: boolean;
}

export const TableRowTemplate = <T,>({
  useDefaultActions = true,
  dataRow,
  dataFields,
  actions,
  selected,
  onSelected,
  onDelete,
}: TableRowTemplateProps<T>) => {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const handleOpenActions = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };
  const handleCloseActions = () => {
    setAnchorEl(null);
  };
  const actionsIsOpen = Boolean(anchorEl);

  const renderValue = (field: DataField<T>, value: any): React.ReactNode => {
    if (typeof field === "object") {
      if (field.render && field.formatter) {
        return field.render(field.formatter(value, dataRow), dataRow);
      } else if (field.render) {
        return field.render(value, dataRow);
      } else if (field.formatter) {
        return field.formatter(value, dataRow);
      }
    }
    switch (typeof value) {
      case "boolean":
        return value ? "Yes" : "No";
      case "object":
        if (Array.isArray(value)) {
          return value.join(", ");
        }
        // if date
        if (value instanceof Date) {
          return value.toLocaleDateString() + " " + value.toLocaleTimeString();
        }
        return JSON.stringify(value);
      case "string":
        return value;
      case "number":
        return value;
      case "bigint":
        return String(value);
      case "symbol":
        return String(value);
      case "undefined":
        return "";
      default:
        return "unsupported type";
    }
  };

  return (
    <TableRow
      aria-checked={selected}
      tabIndex={-1}
      selected={selected}
      sx={{ "& td": { bgcolor: "background.paper", border: 0, p: 2 } }}
    >
      {onSelected && (
        <TableCell
          padding="checkbox"
          sx={{ borderTopLeftRadius: "1rem", borderBottomLeftRadius: "1rem" }}
        >
          <Checkbox
            color="primary"
            checked={selected}
            onClick={() => onSelected(dataRow)}
          />
        </TableCell>
      )}
      {dataFields.map((field, i) => (
        <TableCell key={`table-cell-${i}`}>
          {renderValue(field, dataRow[getDataField(field)])}
        </TableCell>
      ))}
      {useDefaultActions && (
        <TableCell key={`table-actioncell`}>
          <IconButton
            id="user-row-menu-button"
            aria-label="user actions"
            aria-controls="user-row-menu"
            aria-haspopup="true"
            aria-expanded={actionsIsOpen ? "true" : "false"}
            // disabled={processing}
            onClick={handleOpenActions}
          >
            <MoreVert />
          </IconButton>
          <ActionMenu
            onDelete={onDelete}
            row={dataRow}
            anchorEl={anchorEl}
            open={actionsIsOpen}
            actions={actions}
            onClose={handleCloseActions}
          />
        </TableCell>
      )}
    </TableRow>
  );
};

interface ActionProps<T> {
  label: string;
  onClick: (row: T) => void;
  icon: OverridableComponent<SvgIconTypeMap<{}, "svg">> & {
    muiName: string;
  };
}

interface ActionMenuProps<T> {
  row: T;
  anchorEl: HTMLElement | null;
  actions?: ActionProps<T>[];
  open: boolean;
  onClose: () => void;
  onDelete?: (row: T) => Promise<void>;
}

const ActionMenu = <T,>({
  open,
  anchorEl,
  actions,
  onClose,
  onDelete,
  row,
}: ActionMenuProps<T>) => {
  return (
    <Menu
      id="user-row-menu"
      anchorEl={anchorEl}
      aria-labelledby="user-row-menu-button"
      open={open}
      onClose={onClose}
      anchorOrigin={{
        vertical: "top",
        horizontal: "right",
      }}
      transformOrigin={{
        vertical: "top",
        horizontal: "right",
      }}
    >
      {actions?.map((action, i) => (
        <MenuItem
          key={`action-${i}`}
          onClick={() => {
            action.onClick(row);
            onClose();
          }}
        >
          <ListItemIcon>
            <action.icon />
          </ListItemIcon>{" "}
          {action.label}
        </MenuItem>
      ))}
      {onDelete && (
        <MenuItem
          key={`action-delete`}
          onClick={() => {
            onDelete(row);
            onClose();
          }}
        >
          <ListItemIcon>
            <DeleteIcon />
          </ListItemIcon>{" "}
          Delete
        </MenuItem>
      )}
    </Menu>
  );
};

const SortMenu = <T,>({
  setSort,
  dataFields,
  sort,
}: {
  dataFields?: TableTemplateProps<T>["dataFields"];
  setSort: React.Dispatch<React.SetStateAction<SortOptions<T> | null>>;
  sort?: SortOptions<T>;
}) => {
  const [sortMenuAnchorEl, setSortMenuAnchorEl] = useState<null | HTMLElement>(
    null
  );
  const [orderingMenuAnchor, setOrderingMenuAnchor] =
    useState<null | HTMLElement>(null);

  const sortByMenuOpen = Boolean(sortMenuAnchorEl);
  const orderByMenuOpen = Boolean(orderingMenuAnchor);

  return (
    <>
      {sort && (
        <>
          <IconButton
            id="sort-order-button"
            aria-label="sort order"
            aria-controls="sortorder-menu"
            aria-haspopup="true"
            aria-expanded={orderByMenuOpen ? "true" : "false"}
            // disabled={loading}
            onClick={(e) => setOrderingMenuAnchor(e.currentTarget)}
          >
            <SortByAlpha />
          </IconButton>
          <Menu
            id="sortorder-menu"
            anchorEl={orderingMenuAnchor}
            open={Boolean(orderingMenuAnchor)}
            onClose={() => setOrderingMenuAnchor(null)}
            anchorOrigin={{
              vertical: "top",
              horizontal: "right",
            }}
            transformOrigin={{
              vertical: "top",
              horizontal: "right",
            }}
          >
            {[
              { label: "Ascending", value: "asc" },
              { label: "Descending", value: "desc" },
            ].map((option, i) => (
              <MenuItem
                key={`sortorder-${option.value}-${i}`}
                onClick={async () => {
                  setSort((prevSort) => ({
                    field: prevSort?.field as any,
                    fieldAlias: prevSort?.fieldAlias,
                    direction: option.value as any,
                  }));
                  setOrderingMenuAnchor(null);
                }}
              >
                {option.label} {sort?.direction === option.value ? "✔" : ""}
              </MenuItem>
            ))}
          </Menu>
        </>
      )}
      <IconButton
        id="sort-menu-button"
        aria-label="sort by field"
        aria-controls="sortby-menu"
        aria-haspopup="true"
        aria-expanded={Boolean(sortMenuAnchorEl) ? "true" : "false"}
        // disabled={loading}
        onClick={(e) => setSortMenuAnchorEl(e.currentTarget)}
      >
        <Sort />
      </IconButton>
      <Menu
        id="sort-menu"
        anchorEl={sortMenuAnchorEl}
        open={sortByMenuOpen}
        onClose={() => setSortMenuAnchorEl(null)}
        anchorOrigin={{
          vertical: "top",
          horizontal: "right",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "right",
        }}
      >
        <MenuItem key={`sortby-title`} style={{ fontWeight: "bold" }}>
          Sort by
        </MenuItem>
        {dataFields?.map((field, i) => {
          if (typeof field === "object" && field.sortOptions) {
            if (field.sortOptions.disabled) {
              return null;
            }
            return (
              <MenuItem
                key={`sortby-${field.sortOptions.sortFieldName as string}-${i}`}
                onClick={async () => {
                  if (sort?.fieldAlias === field.sortOptions?.sortFieldName) {
                    // clear sort if the same field is clicked
                    setSort(null);
                    setSortMenuAnchorEl(null);
                    return;
                  }
                  setSort({
                    field: field.value,
                    fieldAlias: field.sortOptions?.sortFieldName,
                    direction: sort?.direction || "desc",
                  });
                  setSortMenuAnchorEl(null);
                }}
              >
                {field.sortOptions.sortFieldName as string}{" "}
                {sort?.fieldAlias === field.sortOptions.sortFieldName
                  ? "✔"
                  : ""}
              </MenuItem>
            );
          }
          const fVal = getDataField(field);
          return (
            <MenuItem
              key={`sortby-${fVal as string}-${i}`}
              onClick={async () => {
                if (sort?.field === fVal) {
                  // clear sort if the same field is clicked
                  setSort(null);
                  setSortMenuAnchorEl(null);
                  return;
                }
                setSort({
                  field: fVal,
                  direction: sort?.direction || "desc",
                });
                setSortMenuAnchorEl(null);
              }}
            >
              {fVal as string} {sort?.field === fVal ? "✔" : ""}
            </MenuItem>
          );
        })}
      </Menu>
    </>
  );
};
